/** Admin > Storage: how full the data volume is and what's filling it, the volume itself, and its snapshots. */
import { Camera, HardDrive, RefreshCw } from "lucide-react";
import { useState } from "react";
import { CopyBox, Field, Modal, Spinner } from "../components/ui";
import { api, errorMessage } from "../lib/api";
import { formatDate, timeAgo, toast } from "../lib/app";
import { formatBytes, fullnessTone, percent, refreshAll, URLS, useResource, type FlyVolume, type StorageReport, type VolumeStatus } from "./data";
import { AdminLink, Card, Loading, Notice, PageHead } from "./ui";

const MAX_VOLUME_GB = 500;
const SNAPSHOTS_SHOWN = 8;

export function StoragePage() {
  const storage = useResource<StorageReport>(URLS.storage);
  const volume = useResource<VolumeStatus>(URLS.volume);
  const [scanning, setScanning] = useState(false);

  async function rescan() {
    setScanning(true);
    try {
      await api.get(`${URLS.storage}?fresh=1`);
      await storage.reload();
    } catch (e) {
      toast(errorMessage(e), "error");
    }
    setScanning(false);
  }

  const report = storage.data;
  return (
    <>
      <PageHead
        title="Storage"
        lede="The data volume that holds every project and the database: what's filling it, how big it is, and its backups."
        actions={
          <button className="btn" onClick={rescan} disabled={scanning} title="Measure everything again">
            {scanning ? <Spinner size={12} /> : <RefreshCw size={14} />} Rescan
          </button>
        }
      />

      {!report ? storage.error ? <Notice tone="bad">{storage.error}</Notice> : <Loading>Measuring disk usage…</Loading> : <DiskCard report={report} />}

      <div className="storage-grid">
        {report && <LargestProjects report={report} />}
        <VolumeCard status={volume.data} error={volume.error} reload={volume.reload} />
      </div>

      {volume.data?.apiEnabled && volume.data.volume && <SnapshotsCard status={volume.data} reload={volume.reload} />}
    </>
  );
}

function DiskCard({ report }: { report: StorageReport }) {
  const { disk } = report;
  const projects = report.projects.reduce((a, p) => a + p.files + p.history + p.builds, 0);
  const caches = report.projects.reduce((a, p) => a + p.cache, 0);
  const other = report.database + report.other;
  const fullness = disk.total ? disk.used / disk.total : 0;
  const tone = fullnessTone(fullness);
  const segments = [
    { key: "files", label: "Projects", bytes: projects, hint: "Files, version history and recent PDFs" },
    { key: "cache", label: "Compile caches", bytes: caches, hint: "LaTeX's working files. Safe to clear; the next compile is slower." },
    { key: "other", label: "Database and other", bytes: other, hint: `The database (${formatBytes(report.database)}), server files and filesystem overhead` },
  ];

  return (
    <Card title="Disk" sub={`Measured ${timeAgo(report.scannedAt)}`} className="disk-card">
      <div className="disk-head">
        <div className="disk-figure">
          <span className="disk-big">{formatBytes(disk.used)}</span>
          <span className="muted"> used of {formatBytes(disk.total)}</span>
        </div>
        <span className={`chip chip-${tone}`}>
          {Math.round(fullness * 100)}% full · {formatBytes(disk.free)} free
        </span>
      </div>
      <div className="storage-bar" role="img" aria-label={segments.map((s) => `${s.label} ${percent(s.bytes, disk.total)}`).join(", ")}>
        {segments.map(
          (s) =>
            s.bytes > 0 && (
              <span
                key={s.key}
                className={`storage-seg seg-${s.key}`}
                style={{ flexBasis: `${(s.bytes / disk.total) * 100}%` }}
                title={`${s.label}: ${formatBytes(s.bytes)} (${percent(s.bytes, disk.total)})`}
              />
            ),
        )}
      </div>
      <table className="disk-legend">
        <tbody>
          {segments.map((s) => (
            <tr key={s.key}>
              <td>
                <i className={`swatch seg-${s.key}`} /> {s.label}
              </td>
              <td className="num">{formatBytes(s.bytes)}</td>
              <td className="num muted">{percent(s.bytes, disk.total)}</td>
              <td className="muted small disk-hint">{s.hint}</td>
            </tr>
          ))}
          <tr>
            <td>
              <i className="swatch seg-free" /> Free
            </td>
            <td className="num">{formatBytes(disk.free)}</td>
            <td className="num muted">{percent(disk.free, disk.total)}</td>
            <td className="muted small disk-hint">{fullness >= 0.8 ? "Getting low: clear compile caches or grow the volume." : ""}</td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
}

function LargestProjects({ report }: { report: StorageReport }) {
  const top = report.projects.filter((p) => p.total > 0).slice(0, 5);
  const largest = Math.max(1, ...top.map((p) => p.total));
  return (
    <Card
      title="Largest projects"
      actions={
        <AdminLink to="/admin/projects?sort=size" className="btn btn-sm btn-ghost">
          All projects
        </AdminLink>
      }
    >
      {top.length === 0 ? (
        <p className="admin-empty">No projects yet.</p>
      ) : (
        <ul className="largest">
          {top.map((p) => (
            <li key={p.id}>
              <AdminLink to={`/admin/projects?show=${p.id}`} className="largest-name">
                <b>{p.name}</b> <span className="muted">@{p.owner}</span>
                {p.deletedAt && <span className="chip chip-warn">In recycle bin</span>}
              </AdminLink>
              <span className="largest-size">{formatBytes(p.total)}</span>
              <span className="share-track" title={`${percent(p.total, report.disk.total)} of the disk; ${formatBytes(p.cache)} of it is compile cache`}>
                {p.total - p.cache > 0 && <span className="share-fill seg-files" style={{ width: `${((p.total - p.cache) / largest) * 100}%` }} />}
                {p.cache > 0 && <span className="share-fill seg-cache" style={{ width: `${(p.cache / largest) * 100}%` }} />}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** How to give the server a Fly token, for the pages that need one. */
export function FlySetup({ app, what }: { app: string; what: string }) {
  return (
    <div className="stack fly-setup">
      <p style={{ margin: 0 }}>
        To {what} from here, give the server a Fly token for <b>{app}</b>. Anyone who got root on the server could use it to deploy to the app, so only add it
        if you want this.
      </p>
      <CopyBox value={`fly secrets set FLY_API_TOKEN="$(fly tokens create deploy -a ${app} -x 8760h)" -a ${app}`} />
      <p className="muted small" style={{ margin: 0 }}>
        Or manage it from your terminal: <code>fly volumes list -a {app}</code>, then <code>fly volumes extend &lt;id&gt; -s &lt;GB&gt; -a {app}</code>.
      </p>
    </div>
  );
}

function VolumeCard({ status, error, reload }: { status: VolumeStatus | undefined; error: string | undefined; reload: () => Promise<void> }) {
  const [growing, setGrowing] = useState(false);
  const v = status?.volume;
  const auto = status?.autoExtend;
  return (
    <Card
      title="Volume"
      actions={
        v && (
          <button className="btn btn-sm" onClick={() => setGrowing(true)}>
            <HardDrive size={13} /> Grow…
          </button>
        )
      }
    >
      {!status ? (
        error ? (
          <p className="error-text">{error}</p>
        ) : (
          <Loading>Asking Fly…</Loading>
        )
      ) : !status.app ? (
        <p className="muted admin-empty-text">This server isn't running on Fly, so there's no volume to manage here.</p>
      ) : !status.apiEnabled || !v ? (
        <FlySetup app={status.app} what="grow the volume and take snapshots" />
      ) : (
        <dl className="facts">
          <dt>Name</dt>
          <dd>
            {v.name} <span className="muted mono small">{v.id}</span>
          </dd>
          <dt>Size</dt>
          <dd>
            <b>{v.sizeGb} GB</b> <span className="muted">· can grow, never shrink</span>
          </dd>
          <dt>Grows by itself</dt>
          <dd>
            {auto
              ? `At ${auto.thresholdPercent}% full${auto.addSizeGb ? `, by ${auto.addSizeGb} GB` : ""}${auto.limitGb ? `, up to ${auto.limitGb} GB` : ""}`
              : "No. Grow it by hand when it fills up."}
          </dd>
          <dt>Region</dt>
          <dd>{v.region}</dd>
          <dt>State</dt>
          <dd>
            {v.state}
            {v.encrypted ? " · encrypted" : ""}
          </dd>
          {v.createdAt && (
            <>
              <dt>Created</dt>
              <dd>{formatDate(v.createdAt)}</dd>
            </>
          )}
        </dl>
      )}
      {growing && v && (
        <GrowDialog
          volume={v}
          onClose={() => setGrowing(false)}
          onDone={() => {
            setGrowing(false);
            reload();
            refreshAll("/api/admin/");
          }}
        />
      )}
    </Card>
  );
}

function SnapshotsCard({ status, reload }: { status: VolumeStatus; reload: () => Promise<void> }) {
  const v = status.volume!;
  const [days, setDays] = useState(String(v.snapshotRetention ?? 5));
  const [busy, setBusy] = useState(false);
  const [all, setAll] = useState(false);
  const snapshots = status.snapshots ?? [];
  const shown = all ? snapshots : snapshots.slice(0, SNAPSHOTS_SHOWN);
  const n = Number(days);
  const validDays = Number.isInteger(n) && n >= 1 && n <= 60;

  async function snapshot() {
    setBusy(true);
    try {
      await api.post("/api/admin/storage/volume/snapshots");
      toast("Snapshot started. It can take a few minutes to appear.", "success");
      setTimeout(reload, 5000);
    } catch (e) {
      toast(errorMessage(e), "error");
    }
    setBusy(false);
  }

  async function saveRetention() {
    try {
      await api.post("/api/admin/storage/volume/retention", { days: n });
      toast(`Snapshots are now kept for ${n} days`, "success");
      reload();
    } catch (e) {
      toast(errorMessage(e), "error");
    }
  }

  return (
    <Card
      title="Snapshots"
      sub={
        v.autoBackup === false ? "Fly's daily snapshots are off for this volume." : "Fly takes one a day. They're how you'd get back a lost or broken volume."
      }
      flush
      actions={
        <button className="btn btn-sm" onClick={snapshot} disabled={busy}>
          {busy ? <Spinner size={12} /> : <Camera size={13} />} Take one now
        </button>
      }
    >
      <form
        className="admin-toolbar retention"
        onSubmit={(e) => {
          e.preventDefault();
          if (validDays && n !== v.snapshotRetention) saveRetention();
        }}
      >
        <label className="row">
          <span>{v.autoBackup === false ? "Keep manual snapshots for" : "Keep each snapshot for"}</span>
          <input type="number" min={1} max={60} value={days} onChange={(e) => setDays(e.target.value)} aria-label="Days to keep snapshots" />
          <span>days</span>
        </label>
        <button type="submit" className="btn btn-sm" disabled={!validDays || n === v.snapshotRetention}>
          Save
        </button>
      </form>
      <div className="admin-scroll">
        <table className="table admin-table">
          <thead>
            <tr>
              <th>Taken</th>
              <th className="num">Size</th>
              <th>Kept for</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {status.snapshots === null && (
              <tr>
                <td colSpan={4} className="muted">
                  Couldn't list the snapshots.
                </td>
              </tr>
            )}
            {status.snapshots && snapshots.length === 0 && (
              <tr>
                <td colSpan={4} className="muted">
                  No snapshots yet.
                </td>
              </tr>
            )}
            {shown.map((s) => (
              <tr key={s.id}>
                <td>
                  {formatDate(s.createdAt)} <span className="muted">· {timeAgo(s.createdAt)}</span>
                </td>
                <td className="num">{formatBytes(s.size)}</td>
                <td className="muted">{s.retentionDays ? `${s.retentionDays} days` : "–"}</td>
                <td className="muted">{s.status ?? "–"}</td>
              </tr>
            ))}
            {snapshots.length > SNAPSHOTS_SHOWN && (
              <tr>
                <td colSpan={4}>
                  <button className="btn btn-sm btn-ghost" onClick={() => setAll(!all)}>
                    {all ? "Show fewer" : `Show all ${snapshots.length}`}
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function GrowDialog({ volume, onClose, onDone }: { volume: FlyVolume; onClose: () => void; onDone: () => void }) {
  const [size, setSize] = useState(String(volume.sizeGb + 5));
  const [busy, setBusy] = useState(false);
  const n = Number(size);
  const valid = Number.isInteger(n) && n > volume.sizeGb && n <= MAX_VOLUME_GB;

  async function grow() {
    setBusy(true);
    try {
      const r = await api.post<{ volume: FlyVolume; needsRestart: boolean }>("/api/admin/storage/volume/extend", { sizeGb: n });
      toast(r.needsRestart ? `Grown to ${r.volume.sizeGb} GB. Restart the machine to use the new space.` : `Grown to ${r.volume.sizeGb} GB.`, "success");
      onDone();
    } catch (e) {
      toast(errorMessage(e), "error");
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Grow the volume"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={!valid || busy} onClick={grow}>
            {busy && <Spinner size={12} />} Grow to {valid ? n : "…"} GB
          </button>
        </>
      }
    >
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid && !busy) grow();
        }}
      >
        <Field
          label="New size (GB)"
          hint={`Now ${volume.sizeGb} GB, up to ${MAX_VOLUME_GB}. Volumes can grow but never shrink, and Fly bills for the full size whether or not it's used.`}
        >
          <input type="number" min={volume.sizeGb + 1} max={MAX_VOLUME_GB} value={size} onChange={(e) => setSize(e.target.value)} autoFocus />
        </Field>
        <p className="muted small" style={{ margin: 0 }}>
          The <AdminLink to="/admin/hosting">Hosting</AdminLink> page shows what a bigger volume costs a month.
        </p>
      </form>
    </Modal>
  );
}
